import { Card, Grid, Stack, type SxProps, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useIntl } from "react-intl";
import { type Machine } from "@packsize/schemas/machines";

import { type errorSeverity } from "@/constants";
import {
	getErrorStopReasonMachineMessage,
	getWarningMachineMessages,
	getMachineDisplayStatus,
} from "@/views/dashboard/widgets/components/machine-utils";

import { ClosedToday } from "../closed-today";

import { MachineStatus } from "./machine-status";
import { MachineTracks } from "./machine-tracks";
import { MachineStatusMessage } from "./machine-status-message";

interface ClosingMachineCardProps {
	readonly machine: Machine;
	readonly sx?: SxProps;
}

export const ClosingMachineCard: FC<ClosingMachineCardProps> = ({ machine, sx }) => {
	const [isDismissed, setIsDismissed] = useState(false);
	const hasTracks = machine.machineType === "PL1";
	const statusDisplay = getMachineDisplayStatus(machine);
	const intl = useIntl();

	let message: string | undefined;
	let severity: (typeof errorSeverity)[keyof typeof errorSeverity] | undefined;

	if (statusDisplay.hasError) {
		const errorMachineMessage = getErrorStopReasonMachineMessage(machine);
		message = errorMachineMessage
			? intl.formatMessage(
					{
						id: errorMachineMessage.message,
						defaultMessage: "{translationKey}",
					},
					{ translationKey: errorMachineMessage.message },
				)
			: undefined;
		severity = errorMachineMessage?.severity;
	} else if (statusDisplay.hasWarning) {
		const warningMachineMessages = getWarningMachineMessages(machine);
		if (warningMachineMessages.length > 0) {
			const warningMachineMessage = warningMachineMessages[0];
			message = warningMachineMessage
				? intl.formatMessage(
						{
							id: warningMachineMessage.message,
							defaultMessage: "{translationKey}",
						},
						{ translationKey: warningMachineMessage.message },
					)
				: undefined;
			severity = warningMachineMessage?.severity;
		} else {
			message = undefined;
			severity = undefined;
		}
	}

	const hasStatusMessage = Boolean(message && severity);
	const showStatusMessage = hasStatusMessage && !isDismissed;

	const handleDismiss = () => {
		setIsDismissed(true);
	};

	const handleStatusClick = () => {
		if (isDismissed && hasStatusMessage) {
			setIsDismissed(false);
		}
	};

	return (
		<Card sx={{ borderRadius: 1, display: "flex", flexDirection: "column", flexShrink: 0, boxShadow: 1, ...sx }}>
			<Stack direction="row" padding={1} justifyContent="space-between" alignItems="center" gap={1}>
				<Typography variant="body2">{machine.alias}</Typography>
				<MachineStatus machine={machine} onClick={handleStatusClick} />
			</Stack>
			{hasTracks ? (
				<Grid display="grid" gridTemplateColumns="1fr 2fr" gap={1} sx={{ height: "100%", padding: 1 }}>
					<ClosedToday machineId={machine.id} />
					{showStatusMessage && message && severity ? (
						<MachineStatusMessage message={message} severity={severity} onClose={handleDismiss} />
					) : (
						<MachineTracks machine={machine} />
					)}
				</Grid>
			) : (
				<Stack direction="row" spacing={2} padding={1.5} alignItems="center" height="100%">
					<ClosedToday machineId={machine.id} />
					{showStatusMessage && message && severity ? (
						<MachineStatusMessage message={message} severity={severity} onClose={handleDismiss} />
					) : null}
				</Stack>
			)}
		</Card>
	);
};
